<template>
  <b-button
    class="kst-button"
    :class="theClass"
    :disabled="disabled"
    :href="theHref"
    :icon-left="theIconLeft"
    :label="theLabel"
    :rounded="rounded"
    :tag="theTag"
    :target="theTarget"
    :to="theTo"
    @click="handleClick"
  />
</template>

<script>
import ComponentMixin from "@/mixins/ComponentMixin.js";
import AppCatalog from "@/services/App/AppCatalog.js";
import RouterModel from "@/router/RouterModel.js";

/**
 * Note:
 * - confirm-mode (button) and close-mode (button) are used for selection (i.e. in modal)
 * - cancel-mode (link) and submit-mode (button) are used for form (i.e. in form of new page)
 * - search-mode (button) and clear-mode (button) are used for field (i.e. in date field)
 */
const TAG_LINK = "a";
const TAG_ROUTER_LINK = "router-link";

export default {
  mixins: [ComponentMixin],
  props: {
    disabled: Boolean,
    href: String, // required in mailMode & urlMode
    iconLeft: String,
    label: String,
    rounded: Boolean,
    tag: String,
    target: String,
    to: String, // required in cancelMode

    // ONLY in: actionMode
    fullLabel: Boolean,
    module: Object,
    moduleType: Object,
    urlParams: String,
    // mode: ONLY select 1 (one)
    actionMode: Boolean,
    cancelMode: Boolean,
    clearMode: Boolean,
    closeMode: Boolean,
    confirmMode: Boolean,
    deleteMode: Boolean,
    editMode: Boolean,
    mailMode: Boolean,
    phoneMode: Boolean,
    resetMode: Boolean,
    searchMode: Boolean,
    submitMode: Boolean,
    urlMode: Boolean,
    // additional
    isRound: Boolean
  },
  data: () => ({
    AppCatalog: AppCatalog
  }),
  computed: {
    theClass() {
      return this.getClassName();
    },
    theHref() {
      return this.getHref();
    },
    theIconLeft() {
      return this.getIconLeft();
    },
    theLabel() {
      return this.getLabel();
    },
    theTag() {
      return this.getTagName();
    },
    theTarget() {
      return this.getTarget();
    },
    theTo() {
      return this.getToUrl();
    }
  },
  methods: {
    handleClick() {
      this.$emit(this.$kst.Enum.Event.Click);
    },

    getClassName() {
      let className = "";
      if (this.isRound) {
        className = "k-radius-rounded ";
      }

      const compClasses = this.getComp_Classes();
      if (compClasses !== "") {
        // value of compClasses auto included
        return className;
      }

      if (this.actionMode) {
        return className + AppCatalog.Button.Action.Class;
      }
      else if (this.cancelMode) {
        return className + AppCatalog.Button.Cancel.Class;
      }
      else if (this.clearMode) {
        return className + AppCatalog.Button.Clear.Class;
      }
      else if (this.closeMode) {
        return className + AppCatalog.Button.Close.Class;
      }
      else if (this.confirmMode) {
        return className + AppCatalog.Button.Confirm.Class;
      }
      else if (this.deleteMode) {
        return className + AppCatalog.Button.Delete.Class;
      }
      else if (this.editMode) {
        return className + AppCatalog.Button.Edit.Class;
      }
      else if (this.mailMode) {
        return className + AppCatalog.Button.Mail.Class;
      }
      else if (this.phoneMode) {
        return className + AppCatalog.Button.Phone.Class;
      }
      else if (this.resetMode) {
        return className + AppCatalog.Button.Reset.Class;
      }
      else if (this.searchMode) {
        return className + AppCatalog.Button.Search.Class;
      }
      else if (this.submitMode) {
        return className + AppCatalog.Button.Submit.Class;
      }
      else if (this.urlMode) {
        return className + AppCatalog.Button.Url.Class;
      }

      return undefined;
    },
    getHref() {
      if (this.getTagName() !== TAG_LINK) {
        return undefined;
      }

      if (this.mailMode) {
        return "mailto:" + this.href;
      }
      else if (this.phoneMode) {
        return "tel:" + this.href;
      }

      if (this.href !== undefined) {
        return this.href;
      }

      return undefined;
    },
    getIconLeft() {
      if (this.iconLeft !== undefined) {
        return this.iconLeft;
      }

      if (this.actionMode) {
        if (this.moduleType) {
          return this.moduleType.Icon;
        }
      }
      else if (this.cancelMode) {
        return AppCatalog.Button.Cancel.Icon;
      }
      else if (this.clearMode) {
        return AppCatalog.Button.Clear.Icon;
      }
      else if (this.closeMode) {
        return AppCatalog.Button.Close.Icon;
      }
      else if (this.confirmMode) {
        return AppCatalog.Button.Confirm.Icon;
      }
      else if (this.deleteMode) {
        return AppCatalog.Button.Delete.Icon;
      }
      else if (this.editMode) {
        return AppCatalog.Button.Edit.Icon;
      }
      else if (this.mailMode) {
        return AppCatalog.Button.Mail.Icon;
      }
      else if (this.phoneMode) {
        return AppCatalog.Button.Phone.Icon;
      }
      else if (this.resetMode) {
        return AppCatalog.Button.Reset.Icon;
      }
      else if (this.searchMode) {
        return AppCatalog.Button.Search.Icon;
      }
      else if (this.submitMode) {
        return AppCatalog.Button.Submit.Icon;
      }
      else if (this.urlMode) {
        return AppCatalog.Button.Url.Icon;
      }

      return undefined;
    },
    getLabel() {
      if (this.label !== undefined) {
        return this.label;
      }

      if (this.actionMode) {
        if (this.fullLabel) {
          if (this.moduleType && this.module) {
            return this.moduleType.Name + " " + this.module.FullName;
          }
        }
        else {
          if (this.moduleType) {
            return this.moduleType.Name;
          }
        }
      }
      else if (this.cancelMode) {
        return AppCatalog.Button.Cancel.Label;
      }
      else if (this.clearMode) {
        return AppCatalog.Button.Clear.Label;
      }
      else if (this.closeMode) {
        return AppCatalog.Button.Close.Label;
      }
      else if (this.confirmMode) {
        return AppCatalog.Button.Confirm.Label;
      }
      else if (this.deleteMode) {
        return AppCatalog.Button.Delete.Label;
      }
      else if (this.editMode) {
        return AppCatalog.Button.Edit.Label;
      }
      else if (this.mailMode) {
        return AppCatalog.Button.Mail.Label;
      }
      else if (this.phoneMode) {
        return AppCatalog.Button.Phone.Label;
      }
      else if (this.resetMode) {
        return AppCatalog.Button.Reset.Label;
      }
      else if (this.searchMode) {
        return AppCatalog.Button.Search.Label;
      }
      else if (this.submitMode) {
        return AppCatalog.Button.Submit.Label;
      }
      else if (this.urlMode) {
        return AppCatalog.Button.Mail.Label;
      }

      return undefined;
    },
    getTagName() {
      if (this.tag !== undefined) {
        return this.tag;
      }

      if (this.actionMode || this.cancelMode) {
        return TAG_ROUTER_LINK;
      }
      else if (this.mailMode || this.urlMode || this.phoneMode) {
        return TAG_LINK;
      }

      return undefined;
    },
    getTarget() {
      if (this.target !== undefined) {
        return this.target;
      }

      if (this.actionMode) {
        if (this.moduleType) {
          return this.moduleType.Target;
        }
      }
      else if (this.mailMode || this.urlMode || this.phoneMode) {
        return "_blank";
      }

      return undefined;
    },
    getToUrl() {
      if (this.getTagName() !== TAG_ROUTER_LINK) {
        return undefined;
      }

      if (this.to !== undefined) {
        return this.to;
      }

      if (this.actionMode) {
        if (this.module && this.moduleType) {
          return RouterModel.getPath(this.module, this.moduleType) +
            (this.urlParams === undefined ? "" : this.urlParams);
        }
      }
      else if (this.cancelMode) {
        return this.to;
      }

      return undefined;
    }
  }
}
</script>